// Package imports:
import React, { useState } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faInfoCircle, faExclamationCircle, faExclamationTriangle, IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { faTimes, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import AnimateHeight from 'react-animate-height';
// Component imports:
import Link from '../../ui-elements/Link/Link';

export type AlertType = 'success' | 'info' | 'alert' | 'error';

interface IProps {
    type: AlertType,
    withCloseIcon?: boolean,
    headText: string | JSX.Element,
    text?: JSX.Element | string,
    expandedText?: JSX.Element | string,
    closeAlert?: () => void,
    moreInfoLink?: string,
    iconOverWrite?: IconDefinition
}

// Map of alert types to their corresponding FontAwesome icons
const icon = {
    'success': faCheckCircle,
    'info': faInfoCircle,
    'alert': faExclamationCircle,
    'error': faExclamationTriangle
};

const Alert: React.FC<IProps> = ({
    type,
    withCloseIcon,
    headText,
    text,
    expandedText,
    closeAlert,
    moreInfoLink,
    iconOverWrite
}) => {
    // State to control whether the alert is visible (for dismissing)
    const [show, setShow] = useState(true);

    // State to control whether the expanded content is shown
    const [isExpanded, setIsExpanded] = useState(false);

    // Check if there is expandable content available
    const hasExpandableContent = !!expandedText;

    return (
        <div className={cx('KCL_alert', type, text ? 'lg' : 'sm', { 'dismissed': withCloseIcon && closeAlert === undefined && !show })}>
            {/* Alert header section containing the icon, heading text, and optional close button */}
            <span className="alert-header">
                <div className='icon-and-header'>
                    {/* Display the alert icon - either custom icon override or default based on alert type */}
                    <FontAwesomeIcon icon={iconOverWrite ?? icon[type]} className="icon" />
                    <p className="head-text paragraph-small--bold">{headText}</p>
                </div>

                {/* Close button - only rendered if withCloseIcon is true */}
                {withCloseIcon &&
                    <button
                        onClick={closeAlert ?? (() => { setShow(false) })}
                        className="close-button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                }
            </span>

            {/* Main text content section - only rendered if text prop is provided */}
            {text &&
                <div className="main-text">
                    <div className={cx('paragraph-small')}>
                        {/* Primary text content - this is always visible */}
                        <div>
                            {text}
                        </div>

                        {/* Animated expandable content - only rendered if expandedText is provided */}
                        {hasExpandableContent && (
                            <AnimateHeight
                                duration={300}
                                height={isExpanded ? 'auto' : 0}
                            >
                                {/* Additional content that appears when expanded */}
                                <div className="expanded-content">
                                    {expandedText}
                                </div>
                            </AnimateHeight>
                        )}

                        {/* Expand/collapse toggle button - only shown when there's expandable content */}
                        {hasExpandableContent && (
                            <div
                                className="expand-indicator"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <span className="expand-text">
                                    Nánari upplýsingar
                                </span>
                                {/* Chevron icon that rotates when expanded/collapsed */}
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={cx('chevron-icon', { 'chevron-rotated': isExpanded })}
                                />
                            </div>
                        )}
                    </div>

                    {/* Optional "More information" link - only rendered if moreInfoLink is provided */}
                    {moreInfoLink &&
                        <Link
                            url={moreInfoLink}
                            linkSize="16"
                            targetBlank
                        >
                            Nánari upplýsingar
                        </Link>
                    }
                </div>
            }
        </div>
    );
}

export default Alert;